(ns admin.components.composite.activity-overview.step-card
  (:require
   [admin.components.composite.learning-objective-pill :as lo-pill]
   [admin.components.composite.plo-uploader :as plo-uploader]
   [admin.components.composite.video-preview :as video-preview]
   [admin.components.forms.learning-objective-inline :as lo-inline-form]
   [admin.events.containers.activity.ai-gen :as ai-gen]
   [admin.libs.flip-move :refer [FlipMove]]
   [admin.utils :as au]
   [applied-science.js-interop :as j]
   [clojure.string :refer [capitalize includes? lower-case]]
   [domestic.core :as d]
   [kyber.components.base.button :as button]
   [kyber.components.base.icons :as icon]
   [kyber.components.base.link :as link]
   [kyber.components.base.text :as text]
   [kyber.components.composite.loading :as loading]
   [kyber.components.composite.progress-bar :as progress-bar]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.utils :as ku]
   [potpuri.core :as pot]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]))

(def classes
  (styles->classes
   {:flex-row
    {:display :flex
     :flex-direction :row}

    :flex-column
    {:display :flex
     :flex-direction :column}

    :card-container
    {:display :flex
     :flex-direction :row
     :justify-content :space-between
     :box-shadow (gs [:shadows :shadow-12])
     :margin [[(gs [:spacing :p8])
               (gs [:spacing :p20])
               (gs [:spacing :p8])
               (gs [:spacing :p20])]]
     :padding (gs [:spacing :p20])}

    :details-container
    {:display :flex
     :flex-direction :column
     :padding-top (gs [:spacing :p8])
     :padding-left (gs [:spacing :p40])
     :width "100%"}

    :details-content
    {:padding-left (gs [:spacing :p12])}

    :left-side-container
    {:display :flex
     :flex-direction :column
     :justify-content :space-between
     :width "45%"}

    :right-side-container
    {:display :flex
     :justify-content :center
     :width "45%"
     :flex-direction :column
     :border (str "1px solid " (gs [:colors :gray-scale-100]))
     :border-radius (gs [:radius :r3])}

    :admin-content-container
    {:display :flex
     :justify-content :space-between
     :align-items :center
     :padding (gs [:spacing :p8])
     :border-radius (gs [:radius :r7])
     :border (str "1px solid " (gs [:colors :error-500]))
     :background-color (gs [:colors :error-100])
     :margin-bottom (gs [:spacing :p20])}

    :step-container
    {:display :flex
     :flex-direction :column
     :height "100%"}

    :step-title
    {:display :flex
     :justify-content :space-between}

    :step-index
    {:margin-top (gs [:spacing :p0])
     :margin-bottom (gs [:spacing :p0])}

    :progress-bar-container
    {:display :flex
     :flex-direction :column
     :border-radius (gs [:radius :r3])
     :background-color (gs [:colors :gray-scale-100])
     :padding (gs [:spacing :p12])
     :margin-top (gs [:spacing :p12])}

    :actions-container
    {:display :flex
     :flex-direction :column
     :padding (gs [:spacing :p16])

     "& > *"
     {:border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
      :padding-top (gs [:spacing :p4])
      :padding-bottom (gs [:spacing :p12])

      "&:first-child"
      {:padding-top (gs [:spacing :p0])}

      "&:last-child"
      {:border-bottom :none
       :padding-bottom (gs [:spacing :p0])}}}

    :actions-title-container
    {:display :flex
     :align-items :center
     :padding-bottom (gs [:spacing :p20])}

    :plo-title
    {:padding-left (gs [:spacing :p16])
     :margin-top (gs [:spacing :p0])
     :margin-bottom (gs [:spacing :p0])}

    :rec-item
    {:display :flex
     :align-items :baseline
     :padding [(gs [:spacing :p8])
               (gs [:spacing :p0])]
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))

     "&:last-child"
     {:border-bottom :none}}

    :rec-title-line
    {:display :flex
     :justify-content :space-between
     :align-items :center}

    :rec-description
    ;; Accounts for the show hide label
    {:width "calc(100% - 80px)"
     :transform "scaleY(0)"
     :margin (gs [:spacing :p0])
     :transition "transform 0.3s ease-in-out"
     :height :auto
     :transform-origin :top}

    :show-rec-description
    {:transform "scaleY(1)"
     :margin-top (gs [:spacing :p8])
     :margin-bottom (gs [:spacing :p8])}

    :rec-container
    {:max-height "0px"
     :margin "0px"
     :transition "transform 0.3s ease-in-out"}

    :show-rec-container
    {:max-height "100%"}

    :los-manager-container
    {:display :flex
     :flex-direction :row
     :align-items :center}

    :learning-objective-container
    {:display :flex
     :padding [[(gs [:spacing :p8])
                (gs [:spacing :p24])]]
     :border-radius (gs [:radius :r7])

     "&:hover"
     {:cursor :pointer}}

    :hovering-learning-objective
    {:transform "rotate(-1deg) !important"
     :border (str "1px solid " (gs [:colors :gray-scale-500])
                  " !important")
     :border-radius (gs [:radius :r7])
     :box-shadow (str (gs [:shadows :shadow-24]) "!important")}

    :disabled-learning-objective
    {:background-color (gs [:colors :gray-scale-100])}

    :model-management-container
    {:display :flex
     :flex-direction :column
     :box-shadow (gs [:shadows :shadow-6])
     :padding (gs [:spacing :p16])
     :width "100%"
     :margin-right (gs [:spacing :p24])

     "& > *"
     {:margin-top (gs [:spacing :p4])
      :margin-bottom (gs [:spacing :p4])

      "&:first-child"
      {:margin-top (gs [:spacing :p0])}

      "&:last-child"
      {:margin-bottom (gs [:spacing :p0])
       :border-bottom "none"}}}

    :description
    {:color (gs [:colors :gray-scale-400])
     :margin-top (gs [:spacing :p12])
     :margin-left (gs [:spacing :p36])
     :padding-left (gs [:spacing :p12])
     :border-left (str "2px solid " (gs [:colors :gray-scale-400]))}

    :cta-button
    {:margin-top (gs [:spacing :p16])
     :width :fit-content}

    :plo-uploader-container
    {:max-width "600px"
     :margin-top (gs [:spacing :p24])
     :padding (gs [:spacing :p20])
     :border-radius (gs [:radius :r7])
     :box-shadow (gs [:shadows :shadow-12])}

    :change-plo-button
    {:margin [[(gs [:spacing :p24])
               :auto
               (gs [:spacing :p0])
               :auto]]}

    :no-activity-message
    {:margin-bottom (gs [:spacing :p20])
     :text-align :center}

    :plo-processing-container
    {:display :flex
     :flex-direction :column
     :margin :auto
     :align-items :center
     :justify-content :center
     :width "300px"
     :padding (gs [:spacing :p12])}

    :plo-processing
    {:text-align :center
     :padding-top (gs [:spacing :p16])}

    :grip-bar
    {:display :flex
     :align-items :center
     :color (gs [:colors :gray-scale-600])
     :padding-right (gs [:spacing :p12])}

    :ai-container
    {:display :flex
     :flex-direction :column
     :margin :auto
     :align-items :center
     :justify-content :center
     :width "auto"
     :padding (gs [:spacing :p12])}}))

(d/defdispatcher dispatcher {})

(d/defevent dispatcher :toggle-prop
  [state prop-key]
  (swap! state update prop-key not))

(d/defevent dispatcher :set-prop
  [state prop-key prop-value]
  (swap! state assoc prop-key prop-value))

(defn- get-element-idx
  [element]
  (when (some? element)
    (let [data-idx (.getAttribute element "data-idx")]
      (if (some? data-idx)
        (js/parseInt data-idx)
        (get-element-idx (.-parentNode element))))))

(defn- determine-system-feedback-state
  [{:keys [model system-feedback]} {:keys [state]}]
  ;; There is a special usecase where the system-feedback prop should not be used
  ;; (as of right now, this should probably be moved to the server) - when working
  ;; with uploading PLOs, it should force itself into a :warning state.
  (let [{:keys [show-recommendations]} @state]
    (if (and (= model "plo")
             (false? show-recommendations))
      :warning
      system-feedback)))

(defn- get-icon-settings
  [system-feedback-state]
  (case (keyword system-feedback-state)
    :success {:color (gs [:colors :green-500]) :icon :check-circle}
    :warning {:color (gs [:colors :gray-scale-400]) :icon :warning-square}
    :fail {:color (gs [:colors :warning-600]) :icon :exclamation-circle}

    {:color (gs [:colors :warning-600]) :icon :exclamation-circle}))

(defn- pad
  [n coll val]
  (take n (concat coll (repeat val))))

(defn- rec-item
  [rec-toggle-prop {:keys [title status description]} {:keys [dispatch state]}]
  (let [show-description (get @state rec-toggle-prop)
        icon-settings (get-icon-settings status)]
    [:div {:class (:rec-item classes)}
     [icon/icon {:style {:color (:color icon-settings)
                         :margin-right (gs [:spacing :p8])}}
      (:icon icon-settings)]

     [:div {:style {:width "100%"}}
      [:div {:class (:rec-title-line classes)}
       [text/caption-30 :p title]

       (when (seq description)
         [button/button-link {:class (:details-content classes)
                              :on-click #(dispatch [:toggle-prop rec-toggle-prop])}
          (if (true? show-description)
            "Hide"
            "Show")])]

      (when (seq description)
        [:div {:class (cond-> (:rec-container classes)
                        show-description
                        (#(str % " " (:show-rec-container classes))))}
         [text/caption-20 :p
          {:class (cond-> (:rec-description classes)
                    show-description
                    (#(str % " " (:show-rec-description classes))))}
          description]])]]))

(defn render-learning-objective
  [{:keys [activity
           curriculum
           learning-objective
           learning-objective-idx
           learning-objective-count
           learning-objective-record-count]}
   {:keys [state dispatch]}]
  (let [{:keys [hover-idx drag-idx]}
        @state

        {activity-uuid :activity/uuid}
        activity

        {curriculum-uuid :curriculum/uuid}
        curriculum

        {:learning-objective/keys [description label disabled]}
        learning-objective

        show-lo-form-key (keyword (str "show-lo-form-" learning-objective-idx))

        is-hovering
        (and (not= learning-objective-idx drag-idx)
             (= learning-objective-idx hover-idx))]
    [:li {:style {:list-style :none}}
     (cond
       (get @state show-lo-form-key)
       [lo-inline-form/render
        {:resource-name "learning-objective"
         :activity-uuid activity-uuid
         :curriculum-uuid curriculum-uuid
         :close #(dispatch [:toggle-prop show-lo-form-key])
         :learning-objective-count learning-objective-record-count
         :learning-objective learning-objective}]

       (and (nil? learning-objective)
            (<= learning-objective-count 3))
       [:div {:class (:flex-row classes)}
        [lo-pill/render {:label label}]
        [button/button-link
         {:class (:details-content classes)
          :data-test "edit-learning-objective"
          :on-click #(dispatch [:toggle-prop show-lo-form-key])}
         "Edit"]]

       (and (nil? learning-objective)
            (> learning-objective-count 3)
            (nil? drag-idx))
       [button/button-link
        {:on-click #(dispatch [:toggle-prop show-lo-form-key])}
        "Add Learning Objective"]

       (seq learning-objective)
       [:div
        {:class (cond-> [(:learning-objective-container classes)]
                  (true? disabled)
                  (conj (:disabled-learning-objective classes))
                  (true? is-hovering)
                  (conj (:hovering-learning-objective classes)))
         :data-idx learning-objective-idx
         :draggable (not disabled)
         :on-drag-start #(do (dispatch [:set-prop :drag-idx learning-objective-idx])
                             (-> %
                                 (j/get :dataTransfer)
                                 (j/call :setData "text" learning-objective-idx)))

         :on-drag-enter
         #(let [element-idx (get-element-idx (j/get % :target))]
            (.preventDefault %)
            (dispatch [:set-prop :hover-idx element-idx]))

         :on-drag-leave
         #(do
            (.preventDefault %)
            (dispatch [:set-prop :hover-idx nil]))

         :on-drag-over
         #(let [element-idx (get-element-idx (j/get % :target))]
            (.preventDefault %)
            (dispatch [:set-prop :hover-idx element-idx]))

         :on-drop
         #(let [end-idx (some-> %
                                (j/get :target)
                                get-element-idx
                                js/parseInt)
                start-idx (some-> %
                                  (j/get :dataTransfer)
                                  (j/call :getData "text")
                                  js/parseInt)]
            (.preventDefault %)
            (dispatch [:set-prop :drag-idx nil])

            (when (and (some? end-idx)
                       (some? start-idx)
                       (not= end-idx start-idx)
                       (seq (:learning-objective/uuid learning-objective)))
              (re-frame/dispatch
               [:learning-objectives/adjust-lo-sort-order
                (pot/map-of activity-uuid curriculum-uuid start-idx end-idx)])))

         :on-drag-end
         #(do
            (.preventDefault %)
            (dispatch [:set-prop :drag-idx nil])
            (dispatch [:set-prop :hover-idx nil]))}

        [:div
         [:div {:class (:flex-row classes)}
          [icon/icon {:class (:grip-bar classes)} :bars]
          [lo-pill/render {:label label}]
          [button/button-link
           {:class (:details-content classes)
            :on-click #(dispatch [:toggle-prop show-lo-form-key])}
           "Edit"]]

         (when description
           [text/caption-10 :p
            {:class (:description classes)}
            description])]])]))

(defn- render-learning-objectives
  [{:keys [activity curriculum]} context]
  (let [{:activity/keys [learning-objectives]} activity
        sorted-los (sort-by :learning-objective/ordinal
                            learning-objectives)
        padded-los (pad (max (- 3 (count sorted-los)) 1) '() nil)
        learning-objective-record-count (count sorted-los)
        learning-objective-count (+ learning-objective-record-count
                                    (count padded-los))
        learning-objective-props
        (pot/map-of activity
                  curriculum
                  learning-objective-count
                  learning-objective-record-count)]
    [:div {:class (:los-manager-container classes)
           :style {:padding-top (gs [:spacing :p16])}}
     [:div
      {:class (:model-management-container classes)}
      ;; Saved Learning objectives
      [FlipMove {:key (count sorted-los)
                 :type-name "ul"
                 :data-idx 0
                 :style {:padding (gs [:spacing :p20])
                         :position :relative
                         :list-style :none}}
       (map-indexed
        (fn [idx learning-objective]
          ^{:key (if (nil? learning-objective)
                   (str (random-uuid))
                   (:learning-objective/uuid
                    learning-objective))}
          [render-learning-objective
           (assoc learning-objective-props
                  :learning-objective learning-objective
                  :learning-objective-idx idx)
           context])
        sorted-los)]
      ;; Placeholder (padded) Learning objectives
      (for [learning-objective padded-los]
        (let [idx (str (random-uuid))]
          ^{:key idx}
          [render-learning-objective
           (assoc learning-objective-props
                  :learning-objective learning-objective
                  :learning-objective-idx idx)
           context]))]]))

(defn- render-plo-uploader
  [{:keys [activity curriculum force-stage] :or {force-stage nil}}
   {:keys [state dispatch]}]
  (let [{:keys [add-plo-message]}
        @state

        {:activity/keys [primary-learning-object plo-type published]}
        activity

        plo-uuid
        (:resource/uuid primary-learning-object)

        plo-label
        (ku/plo->label plo-type)]

    (fn []
      [:div {:class (:plo-uploader-container classes)}
       [text/caption-30 :p
        {:class (:no-activity-message classes)}
        (if (some? plo-uuid)
          "Change your Primary Learning Object"
          add-plo-message)]

       (when (and (some? plo-uuid) published)
         [:div {:class (:admin-content-container classes)}
          [icon/icon {:style {:color (gs [:colors :error-500])
                              :margin-right (gs [:spacing :p8])}}
           :exclamation-circle-solid]
          [:div
           [text/caption-20 :p
            (str "This activity is published. Changing this PLO may have "
                 "undesirable consequences if Learners have already "
                 "started this activity.")]]])
       [plo-uploader/render
        {:activity activity
         :curriculum curriculum
         :activity-resources (:activity/resources activity)
         :force-stage force-stage
         :add-plo-message add-plo-message
         :set-plo-message #(dispatch [:set-prop :add-plo-message %])
         :on-upload-start #(dispatch [:set-prop :upload-started true])
         :on-update-cancel #(when (some? plo-uuid)
                              (dispatch [:toggle-prop :show-recommendations])
                              (dispatch [:toggle-prop :show-form])
                              (dispatch [:set-prop :upload-started false])
                              (dispatch [:set-prop :show-plo-form false]))
         :on-success #(do
                        (re-frame/dispatch [:notifier/create-toast
                                            ;; Toast is displayed for both when an admin uploads a PLO and when an 
                                            ;; admin replaces a PLO with an existing resource and the wording should
                                            ;; reflect that.
                                            {:message (str (capitalize plo-label) " saved.")}])
                        
                        ;; :show-recommendations is set to true to ensure the system-feedback-description
                        ;; is displayed after a PLO is replaced with an existing resource. This is consistent with
                        ;; what is displayed after a refresh.
                        (dispatch [:set-prop :show-recommendations true])

                        (dispatch [:set-prop :show-plo-form false])
                        (dispatch [:set-prop :upload-started false])
                        (dispatch [:set-prop :add-plo-message ""]))}]])))

(defn- render-plo-processing-message [plo-type]
  (let [plo-label (ku/plo->label plo-type)]
    [:div {:class (:plo-processing-container classes)}
     [icon/icon-lg :cogs]
     [text/title-30 :p {:class (:plo-processing classes)}
      (str "Processing " plo-label)]
     [text/caption-20 :p (str "Once your " plo-label " has been processed, you will be "
                              "able to create learning moments using the moment "
                              "editing tools.")]]))

(defn- change-plo-component
  [{:keys [activity] :as props} {:keys [state dispatch] :as context}]
  (let [{:resource/keys [processing?]}
        (:activity/primary-learning-object activity)]
    [:div {:class (:flex-column classes)}
   [render-plo-uploader props context]
   (when (and (-> activity :activity/primary-learning-object some?)
              (not processing?)
              (false? (:upload-started @state)))
     [button/button-link {:class (:change-plo-button classes)
                          :on-click #(do
                                       (dispatch [:toggle-prop :show-recommendations])
                                       (dispatch [:toggle-prop :show-plo-form])
                                       (dispatch [:set-prop :add-plo-message ""]))}
      (str "Cancel and keep current " (-> activity :activity/plo-type ku/plo->label))])]))

(defn- plo-component
  [{:keys [activity curriculum]} {:keys [state dispatch] :as context}]
  (let [{:keys [show-plo-form show-plo-failed-warning]}
        @state

        plo-type
        (:activity/plo-type activity)

        {:resource/keys [failed? processing?] :as plo}
        (:activity/primary-learning-object activity)]
    [:div
     (cond
       (true? (or (nil? plo) show-plo-form))
       [change-plo-component (pot/map-of activity curriculum) context]
       (true? failed?)
       [:div
        (when (true? show-plo-failed-warning)
          [:div {:style {:background-color (gs [:colors :warning-300])
                         :width "100%"
                         :display :flex
                         :align-items :center
                         :padding [[(gs [:spacing :p12])
                                    (gs [:spacing :p20])]]}}
           [icon/icon-lg {:style {:color (gs [:colors :warning-600])
                                  :padding-left (gs [:spacing :p12])
                                  :padding-right (gs [:spacing :p12])}}
            :warning-solid]
           [text/body-30 :p {:style {:color (gs [:colors :true-black])}}
            (str "Error uploading file, "
                 (-> activity :activity/primary-learning-object :resource/name)
                 ". "
                 "Please try again!")]
           [button/icon-button
            {:on-click #(dispatch [:set-prop :show-plo-failed-warning false])}
            :close]])

        [render-plo-uploader
         (assoc (pot/map-of activity curriculum) :force-stage :new-resource)
         context]]

       (true? processing?)
       [render-plo-processing-message plo-type]

       (and (false? processing?)
            (false? failed?))
       [:div {:style {:text-align :center
                      :padding (gs [:spacing :p12])}}
        ^{:key (str (-> activity :activity/primary-learning-object :resource/uuid)
                    (-> activity :activity/primary-learning-object :resource/processing?))}
        (condp = plo-type
          "video" [video-preview/render {:plo (-> activity :activity/primary-learning-object)}]
          "pdf" [icon/icon-lg
                 {:style {:margin "0 auto"
                          :color (gs [:colors :gray-scale-600])}}
                 :file-alt]
          nil)
        [text/body-30 :p {:style {:text-align :center}}
         (-> activity :activity/primary-learning-object :resource/name)]
        [button/button-link {:style {:margin-left :auto
                                     :margin-right :auto
                                     :margin-top (gs [:spacing :p24])}
                             :on-click #(do
                                          (dispatch [:toggle-prop :show-recommendations])
                                          (dispatch [:toggle-prop :show-plo-form]))}
         "Change PLO"]]

       :else
       [change-plo-component (pot/map-of activity curriculum) context])]))

(defn- ai-component
  [{:keys [activity curriculum]}]
  (let [curriculum-uuid (:curriculum/uuid curriculum)
        activity-uuid (:activity/uuid activity)
        primary-learning-object (:activity/primary-learning-object activity)
        plo-processing? (:resource/processing? primary-learning-object)
        plo-failed? (:resource/failed? primary-learning-object)
        user-attributes (re-frame/subscribe [:authentication/user-attributes])
        is-super-user? (some-> @user-attributes :permissions :is-super-user boolean)
        disable-ai-button? (if (or (nil? primary-learning-object)
                                   (not is-super-user?))
                             true
                             (or (nil? plo-processing?)
                                 (nil? plo-failed?)
                                 (true? plo-processing?)
                                 (true? plo-failed?)))
        is-polling?
        (re-frame/subscribe [:kyber.subscriptions.async-request/is-polling?
                             ::ai-gen/async-ai-gen-request])]
    [:div {:class (:ai-container classes)}
     (if is-super-user?
       [text/body-20 :p {:style {:text-align :center :margin-bottom :20px}}
        "Processing takes approximately two minutes.  The new Learning Objectives will appear below when done."]
       [text/body-20 :p {:style {:text-align :center :margin-bottom :20px}}
        "Get ready! AI-powered learning experiences are launching soon for everyone."])
     (if @is-polling?
       [:div
        (loading/circles)]
       [button/primary-button
        {:on-click #(ai-gen/fetch-ai-gen {:curriculum-uuid curriculum-uuid
                                          :activity-uuid activity-uuid})
         :disabled disable-ai-button?}
        "Use AI"])]))

(defn- step-details
  [{:keys [step-index title description point-percentage button-settings model]} _]
  [:div {:class (:left-side-container classes)}
   [:div {:class (:step-title classes)}
    [:div {:class (:step-container classes)}
     [text/body-20 :p {:class (:step-index classes)}
      "Step"]
     [text/display-20 :p {:class (:step-index classes)}
      step-index]]

    [:div {:class (:details-container classes)}
     [text/title-20 :p title]
     [text/body-20 :p description]

     (when (not= model "ai")
       [:div {:class (:progress-bar-container classes)}
        [text/caption-20 :p (str "Design Progress for " title)]
        [progress-bar/render {:percent-filled point-percentage
                              :styles {:height (gs [:spacing :p24])
                                       :background-color
                                       (gs [:colors :true-white])}}]])
     (when button-settings
       [link/primary-anchor-button {:style {:margin-top
                                            (gs [:spacing :p16])}
                                    :href (:uri button-settings)}
        (:label button-settings)])]]])

(defn- step-calls-to-action
  [{:keys [system-feedback-description icon-settings
           model recommendations activity curriculum]}
   {:keys [state] :as context}]
  (let [{:keys [show-recommendations add-plo-message]} @state
        primary-learning-object (:activity/primary-learning-object activity)
        plo-type (:activity/plo-type activity)
        plo-processing? (:resource/processing? primary-learning-object)
        plo-failed? (:resource/failed? primary-learning-object)
        disable-ai-button? (if (nil? primary-learning-object)
                             true
                             (or (nil? plo-processing?)
                                 (nil? plo-failed?)
                                 (true? plo-processing?)
                                 (true? plo-failed?)))

        upload-plo? (includes? (lower-case add-plo-message) "upload")]

    [:div {:class (:right-side-container classes)}
     [:div {:class (:actions-container classes)}
      (when (and (= model "ai")
                 disable-ai-button?)
        [:div {:class (:actions-title-container classes)}
         [icon/icon-lg {:style {:color (gs [:colors :warning-600])}}
          :exclamation-circle]
         [text/body-20 :p {:class (:plo-title classes)}
          "This button is not enabled until you have successfully uploaded a PLO"]])

      (when (and (some? system-feedback-description)
                 (not= :warning-square (:icon icon-settings)))
        [:div {:class (:actions-title-container classes)}
         [icon/icon-lg {:style {:color (:color icon-settings)}}
          (:icon icon-settings)]
         [text/body-20 :p {:class (:plo-title classes)}
          (when (not (and (false? show-recommendations)
                          (= model "plo")))
            system-feedback-description)]])

      (when (and (= model "plo") (= plo-type "pdf") upload-plo?)
        [:div {:class (:actions-title-container classes)}
         [icon/icon-lg {:style {:color (gs [:colors :warning-600])}}
          
          :exclamation-circle]
         [text/body-20 :p {:class (:plo-title classes)}
          "The only accepted file extension is .pdf"]])

      (when (and (= model "plo") (= plo-type "video") upload-plo?)
        [:<>
         [:div {:class (:actions-title-container classes)}
          [icon/icon-lg {:style {:color (gs [:colors :warning-600])}}
           :exclamation-circle]
          [text/body-20 :p {:class (:plo-title classes)}
           "The only accepted file extension is .mp4"]]
         [:div {:class (:actions-title-container classes)}
          [icon/icon-lg {:style {:color (gs [:colors :warning-600])}}
           :exclamation-circle]
          [text/body-20 :p {:class (:plo-title classes)}
           "The file size limit is 5 GB."]]
         [:div {:class (:actions-title-container classes)}
          [icon/icon-lg {:style {:color (gs [:colors :warning-600])}}
           :exclamation-circle]
          [text/body-20 :p {:class (:plo-title classes)}
           "Your primary video should be no longer than 35 minutes."]]])

      (when (and (seq recommendations)
                 show-recommendations)
        [:div {:class (:flex-column classes)}
         [text/body-30 :p
          "Recommendations"]
         (map-indexed
          (fn [idx rec]
            ^{:key rec}
            [rec-item (str model "-rec-" idx) rec context])
          recommendations)])

      (condp = (keyword model)
        :plo [plo-component (pot/map-of activity curriculum) context]
        :ai [ai-component (pot/map-of activity curriculum)]
        :learning-objective [render-learning-objectives
                             (pot/map-of activity curriculum) context]
        nil)]]))

(defn step-card
  [_]
  (let [state (reagent/atom {:add-plo-message "Add your Primary Learning Object"
                             :show-recommendations true
                             :show-description false
                             :hover-idx nil
                             :drag-idx nil
                             :is-dragging false
                             :upload-started false
                             :show-plo-failed-warning true
                             :show-plo-form false})
        dispatch (d/bind-dispatcher dispatcher state)]
    (fn [{:keys [step activity curriculum]}]
      (let [{:keys [step-index
                    title
                    description
                    points-available
                    points-awarded
                    system-feedback
                    system-feedback-description
                    model
                    recommendations]}
            step

            {:keys [show-recommendations]}
            @state

            point-percentage
            (ku/calc-percent-floored points-available points-awarded)

            context
            (pot/map-of dispatch state)

            icon-settings
            (-> (pot/map-of system-feedback show-recommendations model)
                (determine-system-feedback-state context)
                get-icon-settings)

            button-settings
            (au/model->uri {:model model
                            :status (get-in recommendations [0 :status])
                            :activity-uuid (:activity/uuid activity)
                            :curriculum-uuid (:curriculum/uuid curriculum)})

            completed
            (= 1 point-percentage)]

        [:div {:class (:card-container classes)
               :style {:border-left (str "16px solid "
                                         (if completed
                                           (gs [:colors :primary-lite-600])
                                           (gs [:colors :gray-scale-400])))}}

         [step-details
          (pot/map-of step-index
                      title
                      description
                      point-percentage
                      button-settings
                      model)
          context]

         [step-calls-to-action
          (pot/map-of activity
                    curriculum
                    icon-settings
                    system-feedback-description
                    model
                    recommendations)
          context]]))))
